import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { applyServerLocale } from '../i18n'
import { loadSession, setUnauthorizedHandler, type SessionState } from '../lib/api'

/**
 * Oturum durumu - uygulamanin tek kaynagi.
 *
 * ## Oturum bitince HARD REDIRECT YOK
 *
 * Bu, merkezsizlestirilmis tasarimin odulu. Run SUNUCUYA ait ve istemcinin
 * oturumundan BAGIMSIZ uretmeye devam ediyor. Oturum duserse mevcut
 * gorunumun UZERINE bir `<ReauthModal>` aciliyor; yeniden dogrulama
 * basarili olunca istemci `streamRun(runId, after=lastSeq)`i tekrar cagiriyor
 * ve modal acikken uretilen token'lar tek bir ANINDA `resume` cercevesiyle
 * geliyor. Kullanici ne yerini, ne draft'ini, ne yanitini kaybediyor.
 *
 * `/login`e yonlendirmek yalnizca AKTIF RUN'I OLMAYAN bir 401 icin dogru;
 * o karar cagiranda (`expire`i kim tetikliyorsa) veriliyor.
 */

export type SessionStatus = 'loading' | 'ready'

interface SessionContextValue {
  status: SessionStatus
  state: SessionState | null
  /** Kimlik dogrulanmis VE MFA tamamlanmis mi. */
  authenticated: boolean
  /** Sifre dogru ama TOTP bekleniyor. */
  mfaPending: boolean
  /** Oturumu sunucudan yeniden okur (giris/cikis sonrasi). */
  refresh(): Promise<SessionState | null>
  /** Yeniden dogrulama modal'i acik mi. */
  reauthNeeded: boolean
  /** 401 goruldu: modal'i ac. */
  requireReauth(): void
  /** Yeniden dogrulama tamamlandi. */
  reauthDone(): void
  /**
   * Kac kez yeniden dogrulandi.
   *
   * `StreamManager` bunu izliyor: yeniden dogrulamadan sonra yarim kalan
   * run'lari TEKRAR devralmasi gerekiyor. `authenticated` bu akista hic
   * `false` olmadigi icin (modal mevcut gorunumun uzerine aciliyor, oturum
   * state'i sifirlanmiyor) baska bir tetikleyici yok.
   */
  reauthCount: number
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [state, setState] = useState<SessionState | null>(null)
  const [reauthNeeded, setReauthNeeded] = useState(false)
  const [reauthCount, setReauthCount] = useState(0)

  const refresh = useCallback(async () => {
    const next = await loadSession()
    setState(next)
    setStatus('ready')
    if (next?.authenticated) setReauthNeeded(false)
    // Sunucudaki dil tercihi cihaz tahminini EZIYOR: kullanici bunu
    // ayarlarda acikca kaydetti, bu `navigator.language`den guclu bir
    // sinyal. Katalog eksikse indirilene kadar mevcut dil kaliyor -
    // yarim cevrilmis bir ekran gosterilmiyor.
    applyServerLocale(next?.user?.locale)
    return next
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // 401 kancasi: `api.ts` her cagrida bunu tetikliyor (auth rotalari HARIC -
  // orada 401 yanlis sifre demek, oturum kaybi degil).
  useEffect(() => {
    setUnauthorizedHandler(() => setReauthNeeded(true))
    return () => setUnauthorizedHandler(null)
  }, [])

  // Sekmeye donusteki tazeleme: baska bir sekmede cikis yapilmis olabilir.
  //
  // Yalnizca GORUNUR olunca ve yalnizca giris yapilmisken: kimliksiz durumda
  // her sekme donusunde istek atmak gereksiz gurultu.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && state?.authenticated) void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refresh, state?.authenticated])

  const value = useMemo<SessionContextValue>(
    () => ({
      status,
      state,
      authenticated: Boolean(state?.authenticated),
      mfaPending: Boolean(state?.mfa_pending),
      refresh,
      reauthNeeded,
      reauthCount,
      requireReauth: () => setReauthNeeded(true),
      reauthDone: () => {
        setReauthNeeded(false)
        // Sayaci ARTIRIYORUZ: `StreamManager` bunu izleyip yarim kalan
        // run'lari yeniden devraliyor.
        setReauthCount((count) => count + 1)
        void refresh()
      },
    }),
    [status, state, refresh, reauthNeeded, reauthCount],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/** Saglayici disinda da kullanilabilir (yoksa null) - istege bagli yetki gostergeleri icin. */
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext)
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession, SessionProvider icinde cagrilmali')
  return value
}
